/**
 * Ringback Tone Generator
 *
 * Generates a standard US ringback tone (440 Hz + 480 Hz) using the Web Audio API.
 * Pattern: 2 seconds ON, 4 seconds OFF (repeating).
 *
 * Each start/stop cycle creates a fresh AudioContext to avoid browser
 * auto-suspension issues that occur when reusing idle contexts.
 */

let audioContext = null;
let oscillatorA = null;
let oscillatorB = null;
let gainNode = null;
let intervalId = null;
let cleanupTimer = null;
let isPlaying = false;

const RING_DURATION_MS = 2000;
const SILENCE_DURATION_MS = 4000;
const CYCLE_MS = RING_DURATION_MS + SILENCE_DURATION_MS;

const FREQ_A = 440;
const FREQ_B = 480;
const VOLUME = 0.08;

function createFreshContext() {
  try {
    const AudioCtx = window.AudioContext || window.webkitAudioContext;
    if (!AudioCtx) return null;
    return new AudioCtx();
  } catch {
    return null;
  }
}

function createNodes(ctx) {
  try {
    gainNode = ctx.createGain();
    gainNode.gain.setValueAtTime(0, ctx.currentTime);
    gainNode.connect(ctx.destination);

    oscillatorA = ctx.createOscillator();
    oscillatorA.type = "sine";
    oscillatorA.frequency.setValueAtTime(FREQ_A, ctx.currentTime);
    oscillatorA.connect(gainNode);

    oscillatorB = ctx.createOscillator();
    oscillatorB.type = "sine";
    oscillatorB.frequency.setValueAtTime(FREQ_B, ctx.currentTime);
    oscillatorB.connect(gainNode);

    oscillatorA.start();
    oscillatorB.start();
    return true;
  } catch (err) {
    console.warn("[RingbackTone] Failed to create audio nodes:", err);
    return false;
  }
}

function scheduleCycle() {
  if (!gainNode || !audioContext) return;
  try {
    gainNode.gain.cancelScheduledValues(audioContext.currentTime);
    gainNode.gain.setValueAtTime(0, audioContext.currentTime);
    gainNode.gain.linearRampToValueAtTime(VOLUME, audioContext.currentTime + 0.05);
    gainNode.gain.setValueAtTime(VOLUME, audioContext.currentTime + RING_DURATION_MS / 1000 - 0.05);
    gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + RING_DURATION_MS / 1000);
  } catch {
    // Context may have been closed between checks
  }
}

function teardown() {
  if (cleanupTimer) { clearTimeout(cleanupTimer); cleanupTimer = null; }
  if (intervalId) { clearInterval(intervalId); intervalId = null; }

  try { oscillatorA?.stop(); } catch { /* already stopped */ }
  try { oscillatorB?.stop(); } catch { /* already stopped */ }
  oscillatorA?.disconnect();
  oscillatorB?.disconnect();
  gainNode?.disconnect();

  oscillatorA = null;
  oscillatorB = null;
  gainNode = null;

  if (audioContext && audioContext.state !== "closed") {
    try { audioContext.close(); } catch { /* ignore */ }
  }
  audioContext = null;
}

export async function startRingback() {
  if (cleanupTimer) { teardown(); isPlaying = false; }
  if (isPlaying) return;

  const ctx = createFreshContext();
  if (!ctx) return;

  if (ctx.state === "suspended") {
    try { await ctx.resume(); } catch {
      try { ctx.close(); } catch {}
      return;
    }
  }

  if (ctx.state !== "running") {
    try { ctx.close(); } catch {}
    return;
  }

  audioContext = ctx;
  if (!createNodes(ctx)) { teardown(); return; }

  scheduleCycle();
  intervalId = setInterval(scheduleCycle, CYCLE_MS);
  isPlaying = true;
}

export function stopRingback() {
  if (!isPlaying && !cleanupTimer) return;
  if (intervalId) { clearInterval(intervalId); intervalId = null; }

  if (gainNode && audioContext && audioContext.state === "running") {
    try {
      gainNode.gain.cancelScheduledValues(audioContext.currentTime);
      gainNode.gain.setValueAtTime(gainNode.gain.value, audioContext.currentTime);
      gainNode.gain.linearRampToValueAtTime(0, audioContext.currentTime + 0.05);
    } catch { /* ignore */ }
  }

  isPlaying = false;
  cleanupTimer = setTimeout(() => { teardown(); }, 100);
}

export function isRingbackPlaying() {
  return isPlaying;
}
